import { useState } from 'react';
import { __ } from '@wordpress/i18n';
import SbUtils from '../../Utils/SbUtils';

/**
 * Theme Selector Component
 *
 * Displays review alert theme options:
 * - Light: Light theme with blue accents
 * - Minimal: Same as Light (Pro)
 * - Dark: Dark blue theme (Pro)
 * - Minimal Dark: Same as Dark (Pro)
 *
 * Note: Minimal themes use the same styles as their base Light/Dark themes
 */
const ThemeSelector = ( { settings, onChange, isPro, upsellModal } ) => {
    const [ hoveredTheme, setHoveredTheme ] = useState( null );

    const themes = [
        {
            id: 'light',
            theme: 'light',
            label: __( 'Light', 'reviews-feed' ),
            description: __( 'Light theme with blue accents', 'reviews-feed' ),
            isPro: false
        },
        {
            id: 'dark',
            theme: 'dark',
            label: __( 'Dark', 'reviews-feed' ),
            description: __( 'Dark blue theme', 'reviews-feed' ),
            isPro: true
        },
        {
            id: 'minimal',
            theme: 'minimal',
            label: __( 'Minimal', 'reviews-feed' ),
            description: __( 'Minimal light theme', 'reviews-feed' ),
            isPro: true
        },
        {
            id: 'minimal-dark',
            theme: 'minimal-dark',
            label: __( 'Minimal Dark', 'reviews-feed' ),
            description: __( 'Minimal dark theme', 'reviews-feed' ),
            isPro: true
        }
    ];

    // Get current theme value (stored as-is, but rendered using light/dark CSS)
    const currentTheme = settings?.theme || 'light';

    const handleThemeSelect = ( themeOption ) => {
        if ( themeOption.isPro && !isPro ) {
            SbUtils.openUpsellModal( 'reviewAlertTheme', upsellModal );
            return;
        }

        onChange( { theme: themeOption.theme } );
    };

    return (
        <div className="sbr-theme-selector">
            <div className="sbr-theme-selector__grid">
                { themes.map( ( themeOption ) => {
                    const isSelected = currentTheme === themeOption.theme;
                    const isLocked = themeOption.isPro && !isPro;

                    return (
                        <div
                            key={ themeOption.id }
                            className={ `sbr-theme-selector__item ${isSelected ? 'sbr-theme-selector__item--selected' : ''} ${isLocked ? 'sbr-theme-selector__item--locked' : ''}` }
                            onClick={ () => handleThemeSelect( themeOption ) }
                            onMouseEnter={ () => setHoveredTheme( themeOption.id ) }
                            onMouseLeave={ () => setHoveredTheme( null ) }
                        >
                            <div className={ `sbr-theme-selector__preview sbr-theme-selector__preview--${themeOption.theme}` }>
                                {/* Mini popup preview */}
                                <div className="sbr-theme-preview-mini">
                                    <div className="sbr-theme-preview-mini__content">
                                        <div className="sbr-theme-preview-mini__avatar"></div>
                                        <div className="sbr-theme-preview-mini__text">
                                            <div className="sbr-theme-preview-mini__line sbr-theme-preview-mini__line--stars">
                                                { SbUtils.printIcon( 'star' ) }
                                                { SbUtils.printIcon( 'star' ) }
                                                { SbUtils.printIcon( 'star' ) }
                                                { SbUtils.printIcon( 'star' ) }
                                                { SbUtils.printIcon( 'star' ) }
                                            </div>
                                            <div className="sbr-theme-preview-mini__line sbr-theme-preview-mini__line--short"></div>
                                            <div className="sbr-theme-preview-mini__line"></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Lock icon overlay */}
                                { isLocked && (
                                    <div className="sbr-theme-selector__lock">
                                        { SbUtils.printIcon( 'key' ) }
                                    </div>
                                ) }

                                {/* Selected checkmark */}
                                { isSelected && (
                                    <div className="sbr-theme-selector__check">
                                        { SbUtils.printIcon( 'success' ) }
                                    </div>
                                ) }
                            </div>

                            <span className="sbr-theme-selector__label">
                                { themeOption.label }
                                { isLocked && (
                                    <span className="sbr-theme-selector__pro-badge">PRO</span>
                                ) }
                            </span>
                        </div>
                    );
                } ) }
            </div>
        </div>
    );
};

export default ThemeSelector;
